import React, { useState, useEffect } from 'react'
import Page from 'layout/page'
import PageTitle from 'layout/page-title'
import RiskBadge from 'shared/badges/risk-badge'
import SparkLineTable from 'charts/sparkline-table'
import TableFooter from 'shared/tables/table-footer'
import TableLoading from 'shared/tables/table-loading'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import StatusBadge from '../shared/badges/status-badge'
import FormControl from 'react-bootstrap/FormControl'
import InputGroup from 'react-bootstrap/InputGroup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faCreditCard,
  faUserSlash
} from '@fortawesome/free-solid-svg-icons'
import { noDigitMoney } from 'helpers/utils'
import useDepartments from 'api/hooks/use-departments'
import { useEmployees } from 'api/hooks/use-employees'
import { useDebounce } from 'api/hooks/use-debounce'
import TransactionCount from 'shared/filters/transactions-count'
import SortablePopoverHeader from 'shared/tables/sortable-popover-header'
import { useNavigate } from 'react-router-dom'

function Employees() {
  const emptyState = {
    page: 1,
    perPage: 25,
    search: '',
    department: '',
    status: '',
    orderBy: 'analytics_per_transaction',
    orderDirection: 'desc',
    minimumAmount: '',
    maximumAmount: '',
    transactionsCount: ''
  }
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [filterParams, setFilterParams] = useState(emptyState)

  const [activePopoverId, setActivePopoverId] = useState(null)

  const debouncedSearch = useDebounce(search, 300)

  useEffect(() => {
    setFilterParams((filterParams) => ({
      ...filterParams,
      search: debouncedSearch
    }))
  }, [debouncedSearch])

  const { isSuccess: isDepartmentSuccess, data: departments } = useDepartments(
    {}
  )
  const {
    isSuccess,
    isLoading,
    data: employees
  } = useEmployees({ params: filterParams })

  return (
    <Page title="Employees">
      <PageTitle />

      <Row>
        <Col sm="4">
          <InputGroup className="mb-3">
            <InputGroup.Text>
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <FormControl
              placeholder="Search by Employee ID"
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col sm="2">
          {isDepartmentSuccess && (
            <Form.Group>
              <Form.Select
                as="select"
                className="text-info"
                value={filterParams.department}
                onChange={(e) =>
                  setFilterParams({
                    ...filterParams,
                    department: e.target.value
                  })
                }
              >
                <option value="">All Departments</option>
                {departments.map((d) => (
                  <option key={`department-${d.id}`} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
        </Col>

        <Col sm="auto">
          <Form.Group>
            <Form.Select
              as="select"
              value={filterParams.status}
              className="text-info"
              onChange={(e) =>
                setFilterParams({
                  ...filterParams,
                  status: e.target.value
                })
              }
            >
              <option value="">All Statuses</option>
              <option value="active">Enabled</option>
              <option value="inactive">Disabled</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col sm={2}>
          <TransactionCount
            filterParams={filterParams}
            setFilterParams={setFilterParams}
          />
        </Col>
      </Row>

      <SparkLineTable>
        <thead>
          <tr className="text-primary">
            <SortablePopoverHeader
              title="Name"
              filters={filterParams}
              setFilters={setFilterParams}
              column="external_id"
              popoverId="employee-name-sort"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
            <SortablePopoverHeader
              title="Risk"
              filters={filterParams}
              setFilters={setFilterParams}
              column="risk_rate"
              popoverId="employee-risk-sort"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
            <SortablePopoverHeader
              title="Analytics"
              filters={filterParams}
              setFilters={setFilterParams}
              column="analytics_count"
              popoverId="employee-analytics-sort"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
            {/* TODO: <th>Monthly Trend</th> */}
            <SortablePopoverHeader
              title="Transactions"
              filters={filterParams}
              setFilters={setFilterParams}
              column="transactions_count"
              popoverId="employee-transactions-sort"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
            <SortablePopoverHeader
              title="Spend"
              filters={filterParams}
              setFilters={setFilterParams}
              column="transactions_amount"
              popoverId="employee-spend-sort"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
          </tr>
        </thead>
        <tbody>
          {isLoading && <TableLoading columns={5} />}

          {isSuccess &&
            employees.data.map((e) => (
              <tr
                key={`employee-row-${e.id}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/employees/${e.id}`)}
              >
                <td>
                  {e.external_id}
                  <small className="text-muted ms-2">{e.department_name}</small>
                  {e.is_pcard && (
                    <span className="mx-2">
                      <StatusBadge
                        title="PCard"
                        icon={faCreditCard}
                        tooltipText="Employee generated from PCard file"
                      />
                    </span>
                  )}
                  {e.status === 'inactive' ? (
                    <span className="ms-2">
                      <StatusBadge
                        title="Disabled"
                        icon={faUserSlash}
                        tooltipText="No longer an active employee"
                      />
                    </span>
                  ) : null}
                </td>
                <td>
                  <RiskBadge count={e.risk} showCount={true} />
                </td>
                <td>{e.analytics_count !== null ? Math.floor(e.analytics_count).toLocaleString() : '—'}</td>
                {/* TODO: <td data-sparkline={`${fourNumbers()}`}></td>  */}
                <td className="text-muted">
                  {e.transactions_count !== null ? Math.floor(e.transactions_count).toLocaleString() : '—'}
                </td>

                <td className="text-muted">
                  {e.transactions_amount !== null ? noDigitMoney(e.transactions_amount) : '—'}
                </td>
              </tr>
            ))}
        </tbody>
      </SparkLineTable>
      {isSuccess && (
        <TableFooter
          meta={employees.meta}
          params={filterParams}
          setParams={setFilterParams}
        />
      )}
    </Page>
  )
}

export default Employees
